import { ReactNode } from "react";

import {
    DEFAULT_SOLANA_EXPLORER,
    explorerTxUrl,
    MayString,
    colorToAlpha,
    formatAddress,
    formatUsd,
    USD_COMPACT_THRESHOLD
} from "@bridgesplit/utils";
import { CSSObject, Skeleton, SxProps, TooltipProps, Typography, styled } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import {
    BORDER_RADIUS,
    ColorExtension,
    FONT_SIZES,
    FONT_VARIANT_WEIGHTS,
    FONT_WEIGHTS,
    LINE_HEIGHT_MULTIPLIER,
    SPACING,
    useAppPalette,
    useColorFromTag,
    useTextColorMap
} from "../theme";
import { SxType } from "../types";
import { ConditionalLink, TextExternalLink } from "./buttons";
import { Column, Row } from "./containers";
import { Icon, IconType } from "./icons";
import { dottedUnderline, Tooltip } from "./misc";
import { TagWrapper } from "./info";
import { TrackingInfo, useUiTrack } from "../tracking";
import { useCopyAddress } from "../util/hooks";

export type TextVariant = "h1" | "h2" | "h3" | "h4" | "body1" | "body2" | "caption";
export type TextColor =
    "primary" | "secondary" | "body" | "caption" | "disabled" | "error" | "success" | "white" | "info" | "warning";
export type RawTagColor = "body" | "secondary" | "success" | "error" | "info" | "warning" | "disabled";
export type TagColor = RawTagColor | ColorExtension;

export interface TextProps {
    children: ReactNode;
    variant?: TextVariant;
    color?: TextColor;
    sx?: SxType;
    loading?: boolean;
    loadingWidth?: string;
    isLink?: boolean;
    underlineLink?: "hover" | "always" | "never";
    underline?: boolean;
    onClick?: () => void;
    multiLine?: boolean;
    fontWeight?: "normal" | "bold" | "semibold";
    svgColor?: TextColor;
    translate?: "yes" | "no";
    dataTestId?: string;
}

export function Text({
    variant = "body1",
    color = "body",
    loadingWidth,
    loading,
    sx,
    children,
    isLink,
    multiLine,
    underlineLink = "never",
    underline,
    onClick,
    fontWeight,
    svgColor,
    translate = "yes",
    dataTestId
}: TextProps) {
    const getColor = useTextColorMap();
    if (loading) {
        return <TextSkeleton multiLine={multiLine} width={loadingWidth} variant={variant} />;
    }

    const dottedStyle: SxProps = underline ? dottedUnderline() : {};

    return (
        <Typography
            data-testid={dataTestId}
            translate={translate}
            color={getColor(color)}
            variant={variant}
            onClick={onClick}
            sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                textDecoration: isLink && underlineLink === "always" ? "underline" : undefined,
                gap: `${Math.round(FONT_SIZES[variant] / 2)}px`,
                cursor: isLink || onClick ? "pointer" : undefined,
                fontWeight: fontWeight ?? FONT_VARIANT_WEIGHTS[variant],
                ":hover": {
                    textDecoration:
                        isLink && (underlineLink === "always" || underlineLink === "hover") ? "underline" : undefined,

                    color: isLink ? colorToAlpha(getColor(color), 0.6) : undefined
                },
                svg: { fontSize: "inherit", verticalAlign: "middle", color: svgColor ? getColor(svgColor) : undefined },
                ...dottedStyle,
                ...sx
            }}
        >
            {children}
        </Typography>
    );
}

export interface TagTextProps extends Omit<TooltipTextProps, "color" | "variant"> {
    color?: TagColor;
    fullWidth?: boolean;
    px?: number;
    py?: number;
    variant?: "colored" | "basic";
    textVariant?: TextVariant;
    customColor?: string;
    useTooltip?: boolean;
    borderRadius?: number;
}

export function TagText({
    color = "body",
    textVariant = "caption",
    px = 1.25,
    py = 0.5,
    fullWidth,
    sx,
    children,
    variant = "colored",
    customColor,
    useTooltip = true, //Added to prevent <p> nesting errors when used in tooltip
    borderRadius,
    ...otherProps
}: TagTextProps) {
    const baseColor = useColorFromTag(color);
    const derivedColor = customColor ?? baseColor;
    const { isDarkMode, textPrimary } = useAppPalette();

    const commonSx = {
        width: fullWidth ? `calc(100% - ${px * SPACING * 2}px)` : "fit-content",
        borderRadius: borderRadius ?? BORDER_RADIUS,
        px,
        py,
        backgroundColor: colorToAlpha(derivedColor, isDarkMode ? 0.2 : 0.1),
        color: variant === "colored" ? derivedColor : textPrimary,
        svg: { color: derivedColor, fontSize: "inherit" },
        ...sx
    };

    if (useTooltip) {
        return (
            <TooltipText
                variant={textVariant}
                fullWidth={fullWidth}
                sx={commonSx}
                icon={false}
                {...(otherProps as Omit<TooltipTextProps, "color" | "variant">)}
            >
                {children}
            </TooltipText>
        );
    }

    return (
        <Text variant={textVariant} sx={commonSx} {...(otherProps as Omit<TextProps, "color" | "variant">)}>
            {children}
        </Text>
    );
}
export function TagTextAlert({
    children,
    color = "info",
    variant = "body2",
    sx,
    icon,
    flexDirection,
    textSx,
    textColor
}: {
    children: ReactNode;
    variant?: TextVariant;
    color?: TagColor;
    textColor?: TextColor;
    sx?: SxType;
    icon?: IconType | JSX.Element;
    flexDirection?: "row" | "column";
    textSx?: SxType;
}) {
    const baseColor = useColorFromTag(color);

    return (
        <TagWrapper sx={sx} color={color}>
            <Row spacing={1} sx={{ alignItems: "flex-start" }}>
                {icon && (
                    <Text sx={{ svg: { color: baseColor, fontSize: "inherit", mt: 0.25 } }} variant={variant}>
                        {typeof icon === "string" ? <Icon sx={{ color: baseColor }} type={icon} /> : icon}
                    </Text>
                )}

                <Text color={textColor} sx={{ flexWrap: "nowrap", flexDirection, ...textSx }} variant={variant}>
                    {children}
                </Text>
            </Row>
        </TagWrapper>
    );
}

export interface TooltipTextProps extends Omit<TextProps, "children"> {
    helpText?: string | undefined;
    icon?: boolean;
    containerSx?: SxType;
    fullWidth?: boolean;
    children?: ReactNode;
    tooltipProps?: Omit<TooltipProps, "title" | "children" | "fullWidth" | "containerSx">;
    iconPosition?: "left" | "right";
    iconSize?: number;
}
export function TooltipText({
    helpText,
    children,
    containerSx,
    fullWidth,
    icon = true,
    tooltipProps,
    iconPosition = "right",
    iconSize = FONT_SIZES.body2,
    ...otherProps
}: TooltipTextProps) {
    const { textDisabled } = useAppPalette();
    return (
        <Tooltip
            {...tooltipProps}
            placement={tooltipProps?.placement ?? "right"}
            fullWidth={fullWidth}
            containerSx={containerSx}
            title={helpText}
        >
            <Text sx={{ gap: 0.5, alignItems: "center" }} {...otherProps}>
                {iconPosition === "left" && icon && !!helpText && (
                    <Icon sx={{ color: textDisabled, height: `${iconSize}px` }} type="tooltip" />
                )}
                {children}
                {iconPosition === "right" && icon && !!helpText && (
                    <Icon sx={{ color: textDisabled, height: `${iconSize}px` }} type="tooltip" />
                )}
            </Text>
        </Tooltip>
    );
}

export interface TextLinkProps extends TextProps {
    to?: string;
    textSx?: SxType;
    tracking?: TrackingInfo;
}
export function TextLink({ to, color = "body", sx, textSx, tracking, ...otherProps }: TextLinkProps) {
    const getColor = useTextColorMap();
    const track = useUiTrack();

    const mappedColor = getColor(color);

    return (
        <ConditionalLink sx={{ color: mappedColor, width: "fit-content", ...sx }} to={to}>
            <Text
                onClick={tracking ? () => track(tracking) : undefined}
                color={color}
                isLink
                sx={textSx}
                {...otherProps}
            />
        </ConditionalLink>
    );
}

export function TextSkeleton({
    variant,
    width = "100px",
    multiLine
}: {
    variant: TextVariant;
    width?: string;
    multiLine?: boolean;
}) {
    const height = FONT_SIZES[variant] * LINE_HEIGHT_MULTIPLIER;
    if (multiLine) {
        return (
            <Column sx={{ width: "100%" }}>
                {Array(3)
                    .fill(null)
                    .map((_, i) => (
                        <Skeleton key={i} height={height} width="100%" />
                    ))}
            </Column>
        );
    }
    return <Skeleton height={height} width={width} />;
}

export interface AddressTextProps extends Omit<TextProps, "children"> {
    address: MayString;
    explorer?: string;
    prefix?: string;
    showIcon?: boolean;
    showText?: boolean;
}
export function AddressText({
    address,
    variant,
    showIcon = true,
    showText = true,
    explorer = DEFAULT_SOLANA_EXPLORER,
    prefix = "account",
    sx,
    ...otherProps
}: AddressTextProps) {
    const { textSecondary } = useAppPalette();
    return (
        <Text
            variant={variant}
            onClick={() => window.open(`${explorer}${prefix}/${address}`)}
            sx={{
                ":hover": {
                    textDecoration: `underline ${textSecondary} dotted 2px`,
                    textDecorationSkipInk: "auto",
                    textUnderlineOffset: "2px"
                },
                minWidth: "max-content",
                ...sx
            }}
            {...otherProps}
        >
            {showText ? formatAddress(address) : null} {showIcon && <Icon type="arrow-diagonal" />}
        </Text>
    );
}

export function UsdPriceTextWithTooltip({
    rawUsdValue,
    textColor = "caption",
    loading,
    loadingWidth,
    variant
}: {
    rawUsdValue: number | undefined;
    textColor?: TextColor;
    variant?: TextVariant;
    loading: boolean;
    loadingWidth?: string | undefined;
}) {
    const shouldShowFullValue = rawUsdValue && rawUsdValue > USD_COMPACT_THRESHOLD; // truncation starts at 10,000
    const displayValue = formatUsd(rawUsdValue);
    const tooltipTitle = shouldShowFullValue ? formatUsd(rawUsdValue, { showFullUsdValue: true }) : undefined;

    const textContent = (
        <Text color={textColor} variant={variant} loading={loading} translate="no" loadingWidth={loadingWidth}>
            {displayValue}
        </Text>
    );

    return tooltipTitle ? <Tooltip title={tooltipTitle}>{textContent}</Tooltip> : textContent;
}

export function AddressCopy({ address, variant }: { address: string; variant: TextVariant }) {
    const copy = useCopyAddress();
    const { textSecondary } = useAppPalette();

    return (
        <Text
            onClick={() => copy(address)}
            sx={{
                ":hover": {
                    ...underlineSx,
                    textDecorationColor: (theme) => `${colorToAlpha(theme.palette.primary.main, 0.25)} !important`
                },
                transition: "all 0.2s ease",
                minWidth: "max-content",
                display: "flex",
                svg: {
                    height: FONT_SIZES.body2,
                    width: FONT_SIZES.body2,
                    color: textSecondary,
                    verticalAlign: "middle"
                }
            }}
            variant={variant}
        >
            {formatAddress(address)}
            <Icon type="copy" />
        </Text>
    );
}

export function TxnSignature({ signature, variant = "body1" }: { signature: string; variant?: TextVariant }) {
    const { textSecondary } = useAppPalette();
    return (
        <TextExternalLink
            underlineLink
            href={explorerTxUrl(signature)}
            sx={{
                display: "flex",
                svg: {
                    height: FONT_SIZES.body2,
                    width: FONT_SIZES.body2,
                    color: textSecondary,
                    verticalAlign: "middle",
                    display: "inline-block"
                },
                fontSize: FONT_SIZES[variant]
            }}
        >
            {formatAddress(signature)}
            <Icon type="arrow-diagonal" sx={{ ml: 1 }} />
        </TextExternalLink>
    );
}

export const SpanBold = styled("span")(({ theme }) => ({
    fontWeight: FONT_WEIGHTS.bold,
    color: theme.palette.text.primary
}));

export const Span = styled("span")(({ theme }) => ({}));

interface StyledUnderlineProps {
    underline?: boolean;
}

export const ExternalLink = styled("a", { shouldForwardProp: (prop) => prop !== "underline" })<StyledUnderlineProps>(
    ({ theme, underline }) => ({
        color: "inherit",
        display: "inline",
        textDecorationColor: colorToAlpha(theme.palette.primary.main, 0.25),
        ...(underline && underlineSx)
    })
);

const underlineSx: CSSObject = {
    textDecoration: "underline",
    textDecorationStyle: "dashed",
    textDecorationSkipInk: "auto",
    textDecorationThickness: "1px",
    textUnderlineOffset: "4px"
};

export const StyledUnderline = styled("span", {
    shouldForwardProp: (prop) => prop !== "underline"
})<StyledUnderlineProps>(({ theme, underline }) => ({
    textDecorationColor: `${colorToAlpha(theme.palette.primary.main, 0.25)} !important`,
    ...(underline && underlineSx)
}));

export function HoverableText({ underline, ...props }: StyledUnderlineProps & TextProps) {
    return (
        <Text
            {...props}
            sx={{
                ...(underline && underlineSx),
                textDecorationColor: (theme) => colorToAlpha(theme.palette.primary.main, 0.25)
            }}
        >
            {props.children}
        </Text>
    );
}

export const Link = styled(RouterLink, { shouldForwardProp: (prop) => prop !== "underline" })<StyledUnderlineProps>(
    ({ theme, underline }) => ({
        color: "inherit",
        display: "inherit",
        textDecoration: "none",
        textDecorationColor: `${colorToAlpha(theme.palette.primary.main, 0.25)} !important`,
        ...(underline && underlineSx)
    })
);
